import { useState, useCallback } from 'react';
import { uploadPOCFile } from '../../services/api';

const usePOCFileUpload = (campaignId) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadStats, setUploadStats] = useState(null);

  const transformPocData = useCallback((poc, campaignId) => ({
    poc_id: poc.poc_id,
    campaign_id: campaignId,
    poc_budget: null, // Will be set by user later
    poc_name: poc.poc_name || poc.name,
    address: poc.address,
    city: poc.city,
    region: poc.region,
    country: poc.country,
    latitude: poc.latitude,
    longitude: poc.longitude
  }), []);

  const processPOCFiles = useCallback(async (files, existingPocs = []) => {
    if (!files || files.length === 0) {
      setError('Please select at least one file');
      return { success: false, pocs: [] };
    }

    setLoading(true);
    setError(null);
    setUploadStats(null);

    try {
      let allProcessedPocs = [];
      let totalFound = 0;
      let totalNotFound = 0;
      let processedFiles = 0;

      // Process each uploaded file
      for (const file of files) {
        // console.log(`Processing file: ${file.name}`);
        
        const result = await uploadPOCFile(file);
        // console.log(`API response for ${file.name}:`, result);

        // Handle new response structure where POCs are nested under data
        const pocsArray = result.data?.pocs || result.pocs;
        // console.log(`Extracted POCs array for ${file.name}:`, pocsArray);
        
        if (pocsArray && Array.isArray(pocsArray)) {
          // Transform API POCs to match component's expected format
          const transformedPocs = pocsArray.map(poc => 
            transformPocData(poc, campaignId)
          );
          // console.log(`Transformed POCs for ${file.name}:`, transformedPocs);

          allProcessedPocs = [...allProcessedPocs, ...transformedPocs];
          
          // Handle new summary structure
          const foundCount = result.data?.found_count || result.summary?.total_found || pocsArray.length;
          const missingCount = result.data?.missing_count || result.summary?.total_not_found || 0;
          
          totalFound += foundCount;
          totalNotFound += missingCount;
          processedFiles++;
        }
      }

      // Remove duplicates and filter out already selected POCs
      const existingPocIds = existingPocs.map(poc => poc.poc_id);
      // console.log('All processed POCs before filtering:', allProcessedPocs);
      // console.log('Existing POC IDs:', existingPocIds);
      
      const uniquePocs = allProcessedPocs.filter((poc, index, self) => 
        index === self.findIndex(p => p.poc_id === poc.poc_id) && 
        !existingPocIds.includes(poc.poc_id)
      );
      // console.log('Unique POCs after filtering:', uniquePocs);

      // Set upload statistics
      const stats = {
        filesProcessed: processedFiles,
        totalFiles: files.length,
        totalFound,
        totalNotFound,
        newPocsAdded: uniquePocs.length,
        duplicatesSkipped: allProcessedPocs.length - uniquePocs.length
      };

      setUploadStats(stats);

      if (uniquePocs.length === 0) {
        const message = allProcessedPocs.length > 0 
          ? 'All POCs from the uploaded files are already selected.'
          : 'No valid POCs found in the uploaded file(s). Please check that your file contains a "POC ID" column with valid POC identifiers.';
        setError(message);
        return { success: false, pocs: [], stats };
      }

      return { 
        success: true, 
        pocs: uniquePocs, 
        stats,
        message: `Successfully processed ${processedFiles} file(s)!\nFound: ${totalFound} POCs\nNot found: ${totalNotFound} POCs\nAdded: ${uniquePocs.length} new POCs`
      };

    } catch (err) {
      console.error('Failed to process files:', err);
      setError(`Failed to process files: ${err.message}`);
      return { success: false, pocs: [], error: err.message };
    } finally {
      setLoading(false);
    }
  }, [campaignId, transformPocData]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearStats = useCallback(() => {
    setUploadStats(null);
  }, []);

  return {
    loading,
    error,
    uploadStats,
    processPOCFiles,
    clearError,
    clearStats
  };
};

export default usePOCFileUpload;
// Keep the old export name for backward compatibility
export { usePOCFileUpload as usePOCExcelUpload };
